import React from 'react';
import { ActivityDay } from '../api/types';

/**
 * Coin activity per day as a paired mini bar chart: earned above the baseline
 * (violet), spent below it (red). A day with no ledger rows renders as a flat
 * baseline tick rather than a gap, so the shape reads as a timeline — the
 * backend zero-fills the series for exactly that reason.
 *
 * Bars are scaled to the largest single-day magnitude in the window, so the
 * chart shows the *shape* of activity, not absolute volume.
 */
export function ActivitySparkline({ series }: { series: ActivityDay[] }) {
  if (series.length === 0) {
    return <p className="text-xs text-gray-500">No activity data.</p>;
  }

  const peak = Math.max(1, ...series.map((d) => Math.max(d.earned, d.spent)));
  const totalEarned = series.reduce((sum, d) => sum + d.earned, 0);
  const totalSpent = series.reduce((sum, d) => sum + d.spent, 0);
  const totalTx = series.reduce((sum, d) => sum + d.tx_count, 0);

  return (
    <div>
      <div className="flex items-end gap-1 h-16" role="img" aria-label="Coin activity by day">
        {series.map((d) => {
          const earnedPct = (d.earned / peak) * 100;
          const spentPct = (d.spent / peak) * 100;
          const label = `${d.day}: +${d.earned.toLocaleString()} / -${d.spent.toLocaleString()} coins · ${d.tx_count} tx`;
          return (
            <div key={d.day} className="flex-1 flex flex-col justify-end h-full" title={label}>
              <div className="flex-1 flex flex-col justify-end">
                <div
                  className="bg-violet-500/80 rounded-t-sm min-h-[1px]"
                  style={{ height: `${earnedPct / 2}%` }}
                />
              </div>
              <div className="h-px bg-gray-700" />
              <div className="flex-1">
                <div className="bg-red-500/70 rounded-b-sm min-h-[1px]" style={{ height: `${spentPct / 2}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-600">{series[0]?.day}</span>
        <span className="text-[10px] text-gray-500">
          <span className="text-violet-300">+{totalEarned.toLocaleString()}</span>
          {' / '}
          <span className="text-red-300">-{totalSpent.toLocaleString()}</span> · {totalTx} tx
        </span>
        <span className="text-[10px] text-gray-600">{series[series.length - 1]?.day}</span>
      </div>
    </div>
  );
}
